import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Plus, Trash2, Save } from "lucide-react";

export default function SEO() {
  const { activeTenant } = useTenant();
  const [pages, setPages] = useState([]);
  const [draft, setDraft] = useState({ path: "", title: "", description: "", keywords: "", og_image: "" });

  const load = async () => {
    if (!activeTenant) return;
    const { data } = await api.get("/seo-pages");
    setPages(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?.id]);

  const save = async (page) => {
    await api.put("/seo-pages", page);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/seo-pages/${id}`);
    load();
  };
  const create = async (e) => {
    e.preventDefault();
    await save(draft);
    setDraft({ path: "", title: "", description: "", keywords: "", og_image: "" });
  };

  if (!activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Select a tenant.</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="seo-page">
      <div className="mb-6">
        <div className="n-label">SEO · {activeTenant.name}</div>
        <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Page metadata</h1>
      </div>

      <form onSubmit={create} className="n-card p-5 mb-6" data-testid="seo-create-form">
        <div className="font-cab font-bold mb-3">Add page</div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="n-label block mb-1">Path</label>
            <input className="n-input" placeholder="/about" value={draft.path} onChange={(e) => setDraft({ ...draft, path: e.target.value })} required data-testid="seo-new-path" />
          </div>
          <div><label className="n-label block mb-1">Title</label>
            <input className="n-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required data-testid="seo-new-title" />
          </div>
          <div className="col-span-2"><label className="n-label block mb-1">Description</label>
            <textarea rows={2} className="n-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} required data-testid="seo-new-desc" />
          </div>
          <div><label className="n-label block mb-1">Keywords</label>
            <input className="n-input" value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} data-testid="seo-new-keywords" />
          </div>
          <div><label className="n-label block mb-1">OG Image URL</label>
            <input className="n-input" value={draft.og_image} onChange={(e) => setDraft({ ...draft, og_image: e.target.value })} data-testid="seo-new-og" />
          </div>
        </div>
        <button className="n-btn mt-4 flex items-center gap-2" type="submit" data-testid="seo-create-submit">
          <Plus size={14} /> ADD PAGE
        </button>
      </form>

      <div className="space-y-3">
        {pages.map((p) => (
          <SEORow key={p.id} page={p} onSave={save} onDelete={remove} />
        ))}
        {pages.length === 0 && (
          <div className="text-sm text-[var(--n-text-muted)] py-6 text-center font-mono-plex">No SEO pages yet.</div>
        )}
      </div>
    </div>
  );
}

function SEORow({ page, onSave, onDelete }) {
  const [edit, setEdit] = useState(page);
  return (
    <div className="n-card p-5" data-testid={`seo-row-${page.path}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-cab font-bold text-lg">
          <span className="font-mono-plex text-xs text-[var(--n-text-muted)] mr-2">PATH</span>
          {page.path}
        </div>
        <button className="text-[var(--n-danger)] flex items-center gap-1 text-xs" onClick={() => onDelete(page.id)} data-testid={`seo-delete-${page.path}`}>
          <Trash2 size={12} /> delete
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="n-label block mb-1">Title</label>
          <input className="n-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
        </div>
        <div><label className="n-label block mb-1">Keywords</label>
          <input className="n-input" value={edit.keywords || ""} onChange={(e) => setEdit({ ...edit, keywords: e.target.value })} />
        </div>
        <div className="col-span-2"><label className="n-label block mb-1">Description</label>
          <textarea rows={2} className="n-input" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
        </div>
      </div>
      <button onClick={() => onSave(edit)} className="n-btn-blue n-btn mt-3 flex items-center gap-2" data-testid={`seo-save-${page.path}`}>
        <Save size={14} /> SAVE
      </button>
    </div>
  );
}
